import { Injectable } from '@angular/core';
import { EntityState, EntityStore, StoreConfig } from '@datorama/akita';
import { of } from 'rxjs';

import { DocumentRepositoryPort } from 'document/port/document-repository.port';
import { Message } from 'domain/message/message';
import { ModuleRepositoryPort } from 'module/port/module-repository.port';
import { UserRepositoryPort } from 'user/port/user-repository.port';

import { AppLocator } from '../../app.locator';

export interface MessageDocument {
	readonly id: string;
	readonly date: number;
	readonly content: string;
	readonly moduleId: string;
	readonly authorId: string;
	readonly documentId: string;
	readonly visioSessionId: string;
	readonly updateTimestamp: number;
	readonly participantsId: string[];
}

export type MessageState = EntityState<MessageDocument, string>;

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'messages' })
export class MessageStore extends EntityStore<MessageState> {
	constructor() {
		super();
	}

	public get _userRepositoryPort(): UserRepositoryPort {
		return AppLocator.inject<UserRepositoryPort>(UserRepositoryPort);
	}

	public get _moduleRepositoryPort(): ModuleRepositoryPort {
		return AppLocator.inject<ModuleRepositoryPort>(ModuleRepositoryPort);
	}

	public get _documentRepositoryPort(): DocumentRepositoryPort {
		return AppLocator.inject<DocumentRepositoryPort>(DocumentRepositoryPort);
	}

	public asDocument(message: Message): MessageDocument {
		return message
			? {
					moduleId: message.moduleId,
					authorId: message.authorId,
					content: message.content,
					date: message.date.getTime(),
					documentId: message.documentId,
					visioSessionId: message.visioSessionId,
					participantsId: message.participantsId,
					updateTimestamp: message.updateTimestamp,
					id: message.id,
			  }
			: null;
	}

	public asEntity(document: MessageDocument): Message {
		return document
			? new Message(
					document.id,
					new Date(document.date),
					document.content,
					document.moduleId,
					document.authorId,
					document.documentId,
					document.visioSessionId,
					document.updateTimestamp,
					document.participantsId,
					this._userRepositoryPort.observeUserById(document.authorId),
					document.moduleId
						? this._moduleRepositoryPort.observeModuleById(document.moduleId)
						: of(null),
					document.documentId
						? this._documentRepositoryPort.observeDocument(document.documentId)
						: of(null)
			  )
			: null;
	}
}
